import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { PropertyDef, Register } from '../query/types'
import { keepScrolling } from './keepScrolling'

/** The properties a condition can be about, by group in the order of the register; a property without operators cannot be filtered on. */
function groupsOf(register: Register): { group: string; properties: PropertyDef[] }[] {
  const groups = new Map<string, PropertyDef[]>()
  for (const property of register.properties) {
    if (property.operators.length === 0) continue
    groups.set(property.group, [...(groups.get(property.group) ?? []), property])
  }
  return [...groups].map(([group, properties]) => ({ group, properties }))
}

/**
 * "Kies een eigenschap" (design §5.2): the properties of the register in a searchable list, one
 * group per `property.group`. Picking one closes the list and hands over its key.
 */
export function PropertyPicker({
  register,
  value,
  onChange,
}: {
  register: Register
  /** The key of the chosen property; null until one is chosen. */
  value: string | null
  onChange: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const current = register.properties.find((property) => property.key === value)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {current ? current.label : 'Kies een eigenschap'}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          {/* 16 px and 44 px high on a phone: iOS Safari zooms in on a smaller field when it gets focus. */}
          <CommandInput placeholder="Zoek eigenschap…" aria-label="Zoek eigenschap" className="max-md:h-11 max-md:text-base" />
          <CommandList label="Eigenschappen" onWheel={keepScrolling} onTouchMove={keepScrolling}>
            <CommandEmpty>Geen eigenschappen gevonden.</CommandEmpty>
            {groupsOf(register).map(({ group, properties }) => (
              <CommandGroup key={group} heading={group} className="[&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:uppercase">
                {properties.map((property) => (
                  <CommandItem
                    key={property.key}
                    value={property.label}
                    className="max-md:min-h-11"
                    onSelect={() => {
                      onChange(property.key)
                      setOpen(false)
                    }}
                  >
                    {property.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
